import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useApi } from "../lib/useApi";
import { ApiError, type RoleMember } from "../lib/api";
import { ALLOWED_ADMIN_EMAIL_DOMAIN, isAllowedAdminEmail } from "../lib/config";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { CopyableId } from "../components/CopyableId";
import { ROLES, roleLabel, usePermissions } from "../lib/roles";

type Tab = typeof ROLES[keyof typeof ROLES];

const TAB_META: Record<Tab, { label: string; desc: string }> = {
  [ROLES.ADMIN]:      { label: "Admins",     desc: "Portal access baseline — manage portal users and run support actions." },
  [ROLES.DEVS]:       { label: "Devs",       desc: "Read access to the Observability tab. Granted to existing admins." },
  [ROLES.CLOUD_TEAM]: { label: "Cloud team", desc: "Cost view + workload restart/scale controls. Granted to existing admins." },
};

export function AdminUsersPage() {
  const api = useApi();
  const perms = usePermissions();

  // The Devs and Cloud Team tabs are gated on the cloud-team scope — granting / revoking
  // those roles requires cloud-team. Admins-without-cloud-team only see the Admins tab.
  const visibleTabs: Tab[] = perms.canManageOtherRoles
    ? [ROLES.ADMIN, ROLES.DEVS, ROLES.CLOUD_TEAM]
    : [ROLES.ADMIN];

  const [tab, setTab] = useState<Tab>(ROLES.ADMIN);
  const [members, setMembers] = useState<RoleMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // If the active tab disappears (e.g. caller never had cloud-team), force-back to Admins.
  useEffect(() => {
    if (!visibleTabs.includes(tab)) setTab(ROLES.ADMIN);
  }, [tab, visibleTabs]);

  useSetTopbarTabs(visibleTabs.map((id) => ({
    id,
    label: TAB_META[id].label,
    active: tab === id,
    onSelect: () => setTab(id),
  })));

  const load = useCallback(async () => {
    setError(null);
    try {
      setMembers(await api.listRoleMembers());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const meta = TAB_META[tab];

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Admin · {meta.label}</div>
        <h1>{meta.label}</h1>
        <p>{meta.desc}</p>
      </div>

      {error && (
        <section className="panel reveal" data-d="2">
          <div className="body"><Err msg={error} /></div>
        </section>
      )}

      {tab === ROLES.ADMIN ? (
        <AdminsTab members={members} onChanged={load} />
      ) : (
        <AdditionalRoleTab role={tab} members={members} onChanged={load} />
      )}
    </>
  );
}

// ---------------- Admins tab: existing list/add/remove flow ----------------

function AdminsTab({ members, onChanged }: { members: RoleMember[] | null; onChanged: () => Promise<void> }) {
  const api = useApi();
  const [emailInput, setEmailInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const admins = useMemo(
    () => members?.filter((m) => m.roles.includes(ROLES.ADMIN)) ?? null,
    [members],
  );

  async function addAdmin(e: FormEvent) {
    e.preventDefault();
    setAddError(null);
    const email = emailInput.trim();
    if (!email || !isAllowedAdminEmail(email)) {
      setAddError(`Email must end in ${ALLOWED_ADMIN_EMAIL_DOMAIN}`);
      return;
    }
    setAdding(true);
    try {
      await api.addRoleMember(email, [ROLES.ADMIN]);
      setEmailInput("");
      await onChanged();
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setAdding(false);
    }
  }

  async function removeFromPortal(userId: string) {
    setRowError(null);
    setRemovingId(userId);
    try {
      await api.setRoleMembership(userId, []);
      setConfirmRemoveId(null);
      await onChanged();
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <>
      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">+</span> Grant admin
          <span className="hint">existing identity-server user, by email</span>
        </header>
        <div className="body">
          <form className="inline-form" onSubmit={addAdmin}>
            <input
              type="email"
              placeholder={`person${ALLOWED_ADMIN_EMAIL_DOMAIN}`}
              spellCheck={false}
              autoComplete="off"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
            />
            <button
              className={"btn btn-primary" + (adding ? " loading" : "")}
              disabled={adding || !emailInput.trim() || !isAllowedAdminEmail(emailInput)}
            >
              <span className="glyph" /> Add to role
            </button>
          </form>
          <div className="hint" style={{ marginTop: 8 }}>
            Staff only — must be a <span className="mono">{ALLOWED_ADMIN_EMAIL_DOMAIN}</span> email.
          </div>
          {addError && <div style={{ marginTop: 16 }}><Err msg={addError} /></div>}
        </div>
      </section>

      <section className="panel reveal" data-d="3">
        <header>
          <span className="idx">01</span> Current admins
          <span className="hint">drawboard-admin role members</span>
        </header>
        <div className="body">
          {!admins ? <Loading /> : (
            <div style={{ border: "1px solid var(--line)" }}>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Display name</th>
                    <th>User id</th>
                    <th>Also</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.length === 0 && (
                    <tr><td className="empty" colSpan={5}>no admins</td></tr>
                  )}
                  {admins.map((m) => {
                    const isConfirming = confirmRemoveId === m.userId;
                    const extras = m.roles.filter((r) => r !== ROLES.ADMIN);
                    return (
                      <tr key={m.userId}>
                        <td>{m.email ?? <span className="faint">(none)</span>}</td>
                        <td>{m.displayName ?? <span className="faint">—</span>}</td>
                        <td><CopyableId value={m.userId} /></td>
                        <td>
                          {extras.length === 0
                            ? <span className="faint">—</span>
                            : extras.map((r) => <RoleChip key={r} role={r} />)
                          }
                        </td>
                        <td className="actions">
                          {!isConfirming ? (
                            <button className="btn btn-ghost btn-sm" onClick={() => { setConfirmRemoveId(m.userId); setRowError(null); }}>
                              Remove from portal
                            </button>
                          ) : (
                            <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                              <button
                                className={"btn btn-danger btn-sm" + (removingId === m.userId ? " loading" : "")}
                                disabled={removingId === m.userId}
                                onClick={() => void removeFromPortal(m.userId)}
                              >
                                Confirm remove
                              </button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemoveId(null)}>
                                Cancel
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {rowError && <div style={{ marginTop: 12 }}><Err msg={rowError} /></div>}
        </div>
      </section>
    </>
  );
}

// ---------------- Devs / Cloud-team tab: toggle role on existing admins ----------------

function AdditionalRoleTab({
  role,
  members,
  onChanged,
}: {
  role: typeof ROLES.DEVS | typeof ROLES.CLOUD_TEAM;
  members: RoleMember[] | null;
  onChanged: () => Promise<void>;
}) {
  const api = useApi();
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only existing admins are candidates — adding/removing this role on an existing admin.
  const admins = useMemo(
    () => members?.filter((m) => m.roles.includes(ROLES.ADMIN)) ?? null,
    [members],
  );

  async function toggle(member: RoleMember, grant: boolean) {
    setError(null);
    setBusyUserId(member.userId);
    try {
      const next = grant
        ? Array.from(new Set([...member.roles, role]))
        : member.roles.filter((r) => r !== role);
      await api.setRoleMembership(member.userId, next);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <section className="panel reveal" data-d="2">
      <header>
        <span className="idx">01</span> {roleLabel(role)} membership
        <span className="hint">grant {roleLabel(role).toLowerCase()} access to existing admins</span>
      </header>
      <div className="body">
        {!admins ? <Loading /> : admins.length === 0 ? (
          <span className="faint">no admins yet — add some on the Admins tab first</span>
        ) : (
          <div style={{ border: "1px solid var(--line)" }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display name</th>
                  <th>{roleLabel(role)}</th>
                  <th className="actions">Action</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((m) => {
                  const has = m.roles.includes(role);
                  return (
                    <tr key={m.userId}>
                      <td>{m.email ?? <span className="faint">(none)</span>}</td>
                      <td>{m.displayName ?? <span className="faint">—</span>}</td>
                      <td>
                        {has ? <span className="mono" style={{ color: "var(--cyan)" }}>granted</span>
                             : <span className="faint">—</span>}
                      </td>
                      <td className="actions">
                        <button
                          className={"btn btn-sm " + (has ? "btn-ghost" : "btn-primary") + (busyUserId === m.userId ? " loading" : "")}
                          disabled={busyUserId === m.userId}
                          onClick={() => void toggle(m, !has)}
                        >
                          {has ? "Revoke" : "Grant"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {error && <div style={{ marginTop: 12 }}><Err msg={error} /></div>}
      </div>
    </section>
  );
}

// ---------------- shared bits ----------------

function RoleChip({ role }: { role: string }) {
  return (
    <span className="tag" style={{ marginRight: 4, fontSize: 10 }}>{roleLabel(role)}</span>
  );
}

function Loading() {
  return <div className="faint mono" style={{ fontSize: 11, letterSpacing: "0.16em" }}>loading…</div>;
}

function Err({ msg }: { msg: string }) {
  return (
    <div className="callout error">
      <span className="mk">err</span>
      <span>{msg}</span>
    </div>
  );
}
